import { isAnalysisQuantityValid } from "../metrology";
import type { QuantityWithMetrology } from "diagnostics-domain/quantity/metrology";
import { computeMeanOverPeriod } from "diagnostics-domain/aggregationPeriod";
import type { MetrologyOverrides } from "../../api/useMetrologyOverrides";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import computeInternalGap from "../computeInternalGap";

type ComputableAnalysis = {
  id: string;
  date: number;
  quantityValues: {
    [quantityId: string]: {
      measured: number;
      std: number | null;
      simulated: number | null;
      metrology: {
        status:
          | "NO_VALIDATION"
          | "INVALID_AUTO"
          | "INVALID_AUTO_CROSS_FILTER"
          | "INVALID_MANUAL"
          | "INVALID_INHERITED"
          | "VALID_AUTO"
          | "VALID_MANUAL"
          | "VALID_PARTIAL_INPUTS";
        crossInvalidityEvaluatedQuantityId: string | null;
      };
      inputCount: number | null;
      missingSmartArgsCount: number | null;
      isSmart: boolean;
    };
  };
};

type Setup = {
  id: string;
  startDate: string | Date | null;
  simulationOutputs: {
    quantityId: string;
    offsetExpected: number | null;
  }[];
};

const computeGapsAndValues = ({
  activeAnalyses,
  setups,
  quantity,
  metrologyOverrides: _metrologyOverrides,
}: {
  activeAnalyses: ComputableAnalysis[];
  setups: Setup[];
  quantity: QuantityWithMetrology;
  metrologyOverrides: MetrologyOverrides;
}) => {
  const quantitySimulationOutputBySetupId: {
    [setupId: string]: Setup["simulationOutputs"][number];
  } = {};

  for (const setup of setups) {
    const output = setup.simulationOutputs.find(s => s.quantityId === quantity.id);
    if (output) quantitySimulationOutputBySetupId[setup.id] = output;
  }

  const gapsAndValues: { date: number; gap: number; measuredValue: number }[] = [];
  for (const activeAnalysis of activeAnalyses) {
    const setup = getAnalysisSetup(setups, activeAnalysis);
    if (!setup) {
      continue;
    }

    const simulationOutput = quantitySimulationOutputBySetupId[setup.id];
    const expectedOffset = simulationOutput?.offsetExpected ?? 0;

    const quantityValue = activeAnalysis.quantityValues[quantity.id];
    if (!quantityValue) {
      continue;
    }
    const valid = isAnalysisQuantityValid(activeAnalysis, quantity);
    const gap = valid ? computeInternalGap(activeAnalysis, quantity.id, expectedOffset) : null;
    if (gap === null) continue;

    gapsAndValues.push({
      date: activeAnalysis.date,
      gap,
      measuredValue: quantityValue.measured,
    });
  }

  return gapsAndValues;
};

export const computeInternalGapsOverPeriodActiveAnalyses = (
  performanceIndicator: QuantityWithMetrology,
  setups: Setup[],
  metrologyOverrides: MetrologyOverrides,
  activeAnalyses: ComputableAnalysis[],
) => {
  const gapsAndValues = computeGapsAndValues({
    activeAnalyses,
    setups,
    quantity: performanceIndicator, // plus besoin de le préciser, forcément le power quantity ?
    metrologyOverrides,
  });

  if (gapsAndValues.length === 0) return null;
  if (gapsAndValues.length === 1) {
    const [firstElement] = gapsAndValues;
    if (firstElement !== undefined) {
      return { meanLosses: firstElement.gap, meanMeasure: firstElement.measuredValue };
    }
  }

  const meanLosses = computeMeanOverPeriod({
    analysisDates: gapsAndValues.map(f => f.date),
    values: gapsAndValues.map(f => f.gap),
  });

  const meanMeasure = computeMeanOverPeriod({
    analysisDates: gapsAndValues.map(f => f.date),
    values: gapsAndValues.map(f => f.measuredValue),
  });

  return { meanLosses, meanMeasure };
};
